import React, { useState, useEffect } from "react";
import { useHistory, useParams } from "react-router-dom";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import { GetTournament } from "api/rpc/2024-04/facilityAdmin/tournament/tournament";
import { GetLeague } from "api/rpc/2024-04/facilityAdmin/league/league";

import { showError } from "redux/actions/ui";
import { ITournament } from "redux/reducers/models/tournament";
import { ILeague } from "redux/reducers/models/league";

import { useAppDispatch } from "hooks/redux";
import { displayCurrency } from "helpers/Helpers";

import Page from "components/page/Page";
import { IPrizeAccount } from "pages/secure/facility/settings/prizeAccounts/PrizeAccount";
import { GetCreditBook } from "api/rpc/2024-04/facilityAdmin/client/admin/creditBook";
import Callout from "components/callout/Callout";
import DataTable from "components/dataTable/DataTable";
import { Badge } from "components/badge/Badge";

export default function TournamentPrizeAccounts() {
  const history = useHistory();
  const { t, i18n } = useTranslation();
  const dispatch = useAppDispatch();
  const { tournamentId }: any = useParams();

  const [prizeAccounts, setPrizeAccounts] = useState<IPrizeAccount[]>(undefined);
  const [tournaments, setTournaments] = useState<Array<ITournament>>([]);

  useEffect(() => {
    const source = axios.CancelToken.source();

    void loadPrizeAccounts(source.token);
    void loadTournaments(source.token);

    return () => source.cancel();
  }, []);

  async function loadPrizeAccounts(token?: CancelToken) {
    if (prizeAccounts !== undefined) {
      setPrizeAccounts(undefined);
    }
    const { status, data } = await GetCreditBook(
      { type: "prize_account", tournament_id: tournamentId },
      token ? false : true,
      token,
    );

    if (token && token.reason) {
      return;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error loading prize accounts")));
    }
    setPrizeAccounts(status !== StatusCode.OK ? [] : data);
  }

  async function loadTournaments(token?: CancelToken) {
    const { status, data } = await GetTournament(null, token ? false : true, token);

    if (token && token.reason) {
      return;
    }
    if (status !== StatusCode.OK) {
      dispatch(showError(t("Error loading tournaments")));
      return;
    }

    setTournaments(data);
  }

  const primaryAction = {
    content: "New Prize Account",
    action: () => history.push("/admin/tournament/" + String(tournamentId) + "/prizeAccounts/new"),
  };

  return (
    <>
      <Page title={t("Prize Accounts")} primaryAction={primaryAction} narrow>
        {prizeAccounts?.length > 0 ? (
          <DataTable
            columns={[{ label: t("Name") }, { label: t("Event") }, { label: t("Balance") }, { label: "" }]}
            loading={prizeAccounts === undefined}
          >
            {prizeAccounts?.map(account => {
              const prizeTournament = tournaments.filter(tourny => tourny.id === account.tournament_id);

              return (
                <tr
                  key={account.id}
                  onClick={() =>
                    history.push(
                      "/admin/tournament/" + String(tournamentId) + "/prizeAccounts/view/" + String(account.id),
                    )
                  }
                  className="clickable"
                >
                  <td>
                    <p>{account.title}</p>
                  </td>
                  <td>
                    <p>{prizeTournament[0] ? prizeTournament[0].name : ""}</p>
                  </td>

                  <td>
                    <p>{displayCurrency("cad", account.balance)}</p>
                  </td>

                  <td>{account.closed ? <Badge type="error">{t('Closed')}</Badge>:<Badge type="success">{t('Open')}</Badge>}</td>
                </tr>
              );
            })}
          </DataTable>
        ) : (
          <>
            <Callout title={t("No Prize Accounts Found")} content={t("You do not have any prize accounts")} type="info" />
          </>
        )}
      </Page>
    </>
  );
}
